import * as React from "react"
import classNames from "classnames"
import { useMergeRefs } from "@nova/toolkit-pure-hooks"

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
    label?: string
    leftItem?: React.ReactNode
    rightItem?: React.ReactNode
    size?: "default" | "xs" | "sm" | "lg"
    placeholderColor?: "default" | "dark"
}

const BaseInput = React.forwardRef<HTMLInputElement, InputProps & { containerClassName?: string }>(
    (
        {
            label,
            leftItem,
            rightItem,
            containerClassName,
            className,
            size = "default",
            placeholderColor = "default",
            type,
            ...rest
        },
        forwardedRef
    ) => {
        const id = React.useId()
        const localRef = React.useRef<HTMLInputElement>(null)
        const ref = useMergeRefs(localRef, forwardedRef)
        const xs = size === "xs"
        const sm = size === "sm"
        const lg = size === "lg"

        return (
            <div className={classNames("flex flex-col gap-2 w-full", className)}>
                {label && (
                    <label htmlFor={rest.id ?? id} className="text-gray-100">
                        {label}
                    </label>
                )}
                <div
                    onClick={(e) => {
                        if (e.target === localRef.current) return
                        if (type === "datetime-local" || type === "date") {
                            localRef.current?.showPicker()
                        } else {
                            localRef.current?.focus()
                        }
                    }}
                    className={classNames(
                        "flex items-center gap-2 focus-within:ring-1 focus-within:ring-blue-200 group/input cursor-text",
                        xs || sm ? "py-1 px-2" : lg ? "py-1.5 px-3" : "p-2 pl-4",
                        containerClassName
                    )}
                >
                    {leftItem && <div className="text-gray-300">{leftItem}</div>}
                    <input
                        {...rest}
                        type={type}
                        id={rest.id ?? id}
                        ref={ref}
                        className={classNames(
                            "bg-transparent p-0 border-0 flex-1 min-w-0 focus:outline-none focus:ring-0 leading-[140%] [color-scheme:dark]",
                            {
                                "h-6": xs || sm,
                                "text-[12px]": xs,
                                "text-[14px]": !xs && !lg,
                                "text-2xl": lg,
                                "placeholder-gray-300 group-hover/input:placeholder-white-1000 focus:placeholder-white-1000":
                                    placeholderColor === "default",
                                "placeholder-gray-500 group-hover/input:placeholder-gray-100":
                                    placeholderColor === "dark",
                            }
                        )}
                    />
                    {rightItem && <div className="text-gray-300">{rightItem}</div>}
                </div>
            </div>
        )
    }
)

export const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    return (
        <BaseInput
            {...props}
            ref={ref}
            containerClassName={`bg-gray-700 border border-transparent rounded-lg`}
        />
    )
})

export const InputOutlined = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    return (
        <BaseInput
            {...props}
            ref={ref}
            containerClassName={`bg-transparent border border-gray-600 rounded-lg`}
        />
    )
})

export const InputPill = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    return (
        <BaseInput
            {...props}
            ref={ref}
            containerClassName={`bg-transparent border border-gray-600 rounded-full overflow-hidden`}
        />
    )
})

export const InputUnstyled = React.forwardRef<
    HTMLInputElement,
    InputProps & { containerClassName?: string }
>((props, ref) => {
    return <BaseInput {...props} ref={ref} />
})

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: string
}

const BaseTextArea: React.FunctionComponent<TextAreaProps & { containerClassName: string }> = ({
    label,
    containerClassName,
    ...rest
}) => {
    const id = React.useId()
    const ref = React.useRef<HTMLTextAreaElement>(null)

    return (
        <div className="flex flex-col gap-2 w-full">
            {label && (
                <label htmlFor={rest.id ?? id} className="text-gray-100">
                    {label}
                </label>
            )}
            <div
                onClick={() => ref.current?.focus()}
                className={`rounded-lg p-2 pl-4 focus-within:ring-1 focus-within:ring-blue-200 cursor-text ${containerClassName}`}
            >
                <textarea
                    {...rest}
                    id={rest.id ?? id}
                    ref={ref}
                    className="scrollable-area bg-transparent p-0 border-0 w-full resize-none placeholder-gray-300 focus:placeholder-white-1000 focus:outline-none focus:ring-0 text-[14px]"
                />
            </div>
        </div>
    )
}

export const TextArea: React.FunctionComponent<TextAreaProps> = (props) => {
    return <BaseTextArea {...props} containerClassName="bg-gray-700 border border-transparent" />
}

export const TextAreaOutlined: React.FunctionComponent<TextAreaProps> = (props) => {
    return <BaseTextArea {...props} containerClassName="bg-transparent border border-gray-600" />
}
